import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, Plus, Search, Network, List, Calendar, Loader2, Zap, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { CategoryChips } from "@/components/heartbeat/CategoryChips";
import { HeartbeatStats } from "@/components/heartbeat/HeartbeatStats";
import { KnowledgeGraph } from "@/components/heartbeat/KnowledgeGraph";
import { GraphDetailPanel } from "@/components/heartbeat/GraphDetailPanel";
import { ChunkListView } from "@/components/heartbeat/ChunkListView";
import { TimelineView } from "@/components/heartbeat/TimelineView";
import { AddKnowledgeModal } from "@/components/heartbeat/AddKnowledgeModal";
import { HeartbeatSettings } from "@/components/heartbeat/HeartbeatSettings";
// v1.95 — BrandIdentityTab moved to /pages/brands.tsx (Brand detail dialog).
// Heartbeat no longer renders it. The file stays in heartbeat/ folder for now
// as the import path source-of-truth; only the in-Heartbeat surface was removed.

interface Brand {
  id: number;
  name: string;
}

interface GraphNode {
  id: number;
  category: string;
  title: string;
  importance: number;
  createdAt: string | null;
  subcategory: string | null;
  sourceType: string | null;
  brandId?: number | null;
  adAccountId?: number | null;
}

interface GraphEdge {
  source: number;
  target: number;
  relationshipType: string;
  strength: number;
}

interface StatsData {
  totalChunks: number;
  totalConnections: number;
  newThisWeek: number;
  intelligenceScore: number;
  chunksByCategory: Record<string, number>;
}

interface ChunkListResponse {
  chunks: Array<GraphNode & { content: string }>;
  total: number;
  page: number;
  limit: number;
}

interface WeekGroup {
  weekStart: string;
  chunks: Array<GraphNode & { content?: string }>;
  count: number;
}

// v1.95 — "brand-identity" view removed from Heartbeat; now lives on the
// Brand Identity tab inside each brand's detail dialog (Brands page).
type ViewMode = "graph" | "list" | "timeline";

const BRAND_RING_COLORS = ["#6366F1", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6", "#EC4899"];

export function HeartbeatContent() {
  const queryClient = useQueryClient();
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("graph");
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [listSort, setListSort] = useState("newest");
  const [detailOpen, setDetailOpen] = useState(false);

  const { data: allBrands = [], isLoading: brandsLoading } = useQuery<Brand[]>({
    queryKey: ["/api/brands"],
    queryFn: async () => {
      const res = await fetch("/api/brands");
      if (!res.ok) return [];
      return res.json();
    },
  });

  // Auto-select first brand once loaded
  useEffect(() => {
    if (allBrands.length > 0 && selectedBrandId === null) {
      setSelectedBrandId(allBrands[0].id);
    }
  }, [allBrands, selectedBrandId]);

  const brandId = selectedBrandId ?? allBrands[0]?.id ?? null;

  const categoryQuery = selectedCategories.length > 0 ? `categories=${selectedCategories.join(",")}` : "";

  const { data: graphData, isLoading: graphLoading } = useQuery<{ nodes: GraphNode[]; edges: GraphEdge[] }>({
    queryKey: ["/api/heartbeat/brands", brandId, "graph", categoryQuery],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (categoryQuery) params.set("categories", selectedCategories.join(","));
      const res = await fetch(`/api/heartbeat/brands/${brandId}/graph?${params}`);
      if (!res.ok) throw new Error("Failed to fetch brand graph");
      return res.json();
    },
    enabled: !!brandId,
  });

  const { data: statsData } = useQuery<StatsData>({
    queryKey: ["/api/heartbeat/brands", brandId, "stats"],
    queryFn: async () => {
      const res = await fetch(`/api/heartbeat/brands/${brandId}/stats`);
      if (!res.ok) throw new Error("Failed to fetch brand stats");
      return res.json();
    },
    enabled: !!brandId,
  });

  const { data: chunkListData, isLoading: chunksLoading } = useQuery<ChunkListResponse>({
    queryKey: ["/api/heartbeat/brands", brandId, "chunks", listSort],
    queryFn: async () => {
      const res = await fetch(`/api/heartbeat/brands/${brandId}/chunks?sort=${listSort}&limit=200`);
      if (!res.ok) throw new Error("Failed to fetch brand chunks");
      return res.json();
    },
    enabled: !!brandId && viewMode === "list",
  });

  const { data: rawTimelineData = [], isLoading: timelineLoading } = useQuery<WeekGroup[]>({
    queryKey: ["/api/heartbeat/brands", brandId, "timeline"],
    queryFn: async () => {
      const res = await fetch(`/api/heartbeat/brands/${brandId}/timeline?months=3`);
      if (!res.ok) throw new Error("Failed to fetch brand timeline");
      return res.json();
    },
    enabled: !!brandId && viewMode === "timeline",
  });

  const timelineData = useMemo(() => {
    if (selectedCategories.length === 0) return rawTimelineData;
    return rawTimelineData
      .map(week => ({
        ...week,
        chunks: week.chunks.filter((c: { category: string }) => selectedCategories.includes(c.category)),
      }))
      .filter(week => week.chunks.length > 0);
  }, [rawTimelineData, selectedCategories]);

  const stats = statsData ?? null;

  // For multi-brand filter chips (if future multi-brand graph)
  const brandColorMap = useMemo(() => new Map<number, string>(), []);

  const handleSelectNode = (id: number | null) => {
    setSelectedNodeId(id);
    if (id !== null) setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setSelectedNodeId(null);
    setDetailOpen(false);
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/heartbeat/brands", brandId] });
  };

  const [triggeringBackfill, setTriggeringBackfill] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState<string | null>(null);

  const handleTriggerBackfill = async () => {
    if (!brandId) return;
    setTriggeringBackfill(true);
    setBackfillMessage("Starting intelligence scan...");
    try {
      const resp = await fetch(`/api/heartbeat/brands/${brandId}/backfill`, { method: "POST" });
      const data = await resp.json();
      if (!resp.ok) {
        setBackfillMessage(`Error: ${data.error || "Failed to start scan"}`);
        setTriggeringBackfill(false);
        return;
      }
      setBackfillMessage("Scanning brand data and performance metrics... This takes about a minute.");
      const poll = setInterval(async () => {
        try {
          const statsResp = await fetch(`/api/heartbeat/brands/${brandId}/stats`);
          const statsData = await statsResp.json();
          if (statsData.totalChunks > 0) {
            clearInterval(poll);
            setBackfillMessage(null);
            setTriggeringBackfill(false);
            invalidateAll();
          }
        } catch {}
      }, 5000);
      setTimeout(() => {
        clearInterval(poll);
        if (triggeringBackfill) {
          setBackfillMessage("Scan is still running in the background. Refresh the page in a moment.");
          setTriggeringBackfill(false);
        }
      }, 120000);
    } catch (err: unknown) {
      setBackfillMessage(`Error: ${err instanceof Error ? err.message : String(err)}`);
      setTriggeringBackfill(false);
    }
  };

  const isNewAccount = (stats !== null && stats.totalChunks === 0) ||
    (graphData && graphData.nodes.length === 0 && !graphLoading);

  const views = [
    { key: "graph" as const, icon: Network, label: "Graph" },
    { key: "list" as const, icon: List, label: "List" },
    { key: "timeline" as const, icon: Calendar, label: "Timeline" },
    // v1.95 — Brand Identity tab moved to Brands page (Brand detail dialog).
    // Heartbeat is now strictly RAG/embeddings/knowledge graph.
  ];

  return (
    <div className="space-y-5" data-testid="heartbeat-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-heartbeat-title">
            Heartbeat
          </h1>
          <span className="text-sm text-gray-500">AI Intelligence Memory</span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {allBrands.length > 1 && (
            <Select
              value={String(brandId ?? "")}
              onValueChange={v => { setSelectedBrandId(Number(v)); setSelectedNodeId(null); }}
            >
              <SelectTrigger className="w-[220px] h-9 text-sm" data-testid="select-brand">
                <SelectValue placeholder="Select brand" />
              </SelectTrigger>
              <SelectContent>
                {allBrands.map(b => (
                  <SelectItem key={b.id} value={String(b.id)} data-testid={`brand-option-${b.id}`}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {brandId && <HeartbeatSettings brandId={brandId} />}

          <Button
            size="sm"
            onClick={() => setAddModalOpen(true)}
            className="bg-white hover:bg-gray-50 text-xs gap-1.5 border border-[#DCDCDC] text-[#8C8578] hover:border-[#A8A49E]"
            disabled={!brandId}
            data-testid="button-add-knowledge"
          >
            <Plus className="h-3.5 w-3.5" /> Add Knowledge
          </Button>
        </div>
      </div>

      <HeartbeatStats stats={stats} isLoading={graphLoading && viewMode === "graph"} />

      <CategoryChips
        selected={selectedCategories}
        onChange={setSelectedCategories}
        counts={stats?.chunksByCategory}
      />

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search nodes..."
            className="pl-9 h-9 text-sm"
            data-testid="input-search-nodes"
          />
        </div>

        <div className="flex items-center bg-white rounded-lg border border-[#DCDCDC] p-0.5">
          {views.map(v => (
            <button
              key={v.key}
              onClick={() => setViewMode(v.key)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                viewMode === v.key
                  ? "bg-gray-100 text-[#5a534a] border border-[#DCDCDC]"
                  : "text-[#8C8578] hover:text-[#5a534a]"
              )}
              data-testid={`view-mode-${v.key}`}
            >
              <v.icon className="h-3.5 w-3.5" />
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {brandsLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
        </div>
      ) : !brandId ? (
        <div className="rounded-xl border border-gray-200 p-12 text-center" style={{ backgroundColor: '#F8F8F7' }} data-testid="heartbeat-no-brands">
          <Brain className="h-12 w-12 text-gray-300 mb-4 mx-auto" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No brands yet</h2>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Create a brand in the Canvas to start building your Heartbeat knowledge graph.
          </p>
        </div>
      ) : isNewAccount && !graphLoading ? (
        <div className="rounded-xl border border-gray-200 p-12 text-center" style={{ backgroundColor: '#F8F8F7' }} data-testid="heartbeat-empty">
          <Brain className="h-12 w-12 text-gray-300 mb-4 mx-auto" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {triggeringBackfill ? "Scanning your brand..." : "No intelligence data yet"}
          </h2>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            {backfillMessage || "Heartbeat learns from your brand data, ad performance, competitor analysis, and AskMoji conversations. Click below to run the initial scan."}
          </p>
          {triggeringBackfill && (
            <div className="flex items-center justify-center gap-2 mb-4">
              <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
              <span className="text-sm text-indigo-600 font-medium">Processing...</span>
            </div>
          )}
          <Button
            onClick={handleTriggerBackfill}
            disabled={triggeringBackfill}
            className="bg-white hover:bg-gray-50 gap-2 border border-[#DCDCDC] text-[#8C8578] hover:border-[#A8A49E]"
            data-testid="button-trigger-scan"
          >
            {triggeringBackfill ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            {triggeringBackfill ? "Scanning..." : "Trigger Initial Scan"}
          </Button>
        </div>
      ) : (
        <div className={cn("flex gap-4", viewMode === "graph" ? "h-[520px]" : "")}>
          <div className={cn(
            "flex-1 min-w-0",
            viewMode === "graph" && "rounded-xl border border-gray-200 overflow-hidden"
          )} style={viewMode === "graph" ? { backgroundColor: '#F8F8F7' } : undefined}>
            {viewMode === "graph" && (
              graphLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Loading graph...</p>
                  </div>
                </div>
              ) : (
                <KnowledgeGraph
                  nodes={graphData?.nodes ?? []}
                  edges={graphData?.edges ?? []}
                  selectedNodeId={selectedNodeId}
                  onSelectNode={handleSelectNode}
                  searchTerm={searchTerm}
                  brandColorMap={brandColorMap}
                />
              )
            )}

            {viewMode === "list" && (
              <ChunkListView
                chunks={(chunkListData?.chunks ?? []).filter(c => {
                  if (selectedCategories.length > 0 && !selectedCategories.includes(c.category)) return false;
                  if (searchTerm && !c.title.toLowerCase().includes(searchTerm.toLowerCase()) && !c.content.toLowerCase().includes(searchTerm.toLowerCase())) return false;
                  return true;
                })}
                selectedNodeId={selectedNodeId}
                onSelectNode={id => handleSelectNode(id)}
                sortBy={listSort}
                onSortChange={setListSort}
                isLoading={chunksLoading}
              />
            )}

            {viewMode === "timeline" && (
              <TimelineView
                timeline={timelineData}
                selectedNodeId={selectedNodeId}
                onSelectNode={id => handleSelectNode(id)}
                isLoading={timelineLoading}
              />
            )}
          </div>

          <div className={cn(
            "hidden lg:block w-[280px] flex-shrink-0 transition-all",
            !detailOpen && viewMode === "graph" && "lg:block",
          )}>
            <GraphDetailPanel
              selectedNodeId={selectedNodeId}
              brandId={brandId ?? undefined}
              onSelectNode={id => handleSelectNode(id)}
              onClose={handleCloseDetail}
              className="h-full"
            />
          </div>

          {detailOpen && selectedNodeId && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div className="absolute inset-0 bg-black/30" onClick={handleCloseDetail} />
              <div className="absolute right-0 top-0 h-full w-[300px] animate-in slide-in-from-right duration-200">
                <GraphDetailPanel
                  selectedNodeId={selectedNodeId}
                  brandId={brandId ?? undefined}
                  onSelectNode={id => handleSelectNode(id)}
                  onClose={handleCloseDetail}
                  className="h-full rounded-none"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {brandId && (
        <AddKnowledgeModal
          open={addModalOpen}
          onOpenChange={setAddModalOpen}
          brandId={brandId}
          onCreated={invalidateAll}
        />
      )}
    </div>
  );
}

export default function HeartbeatPage() {
  return (
    <Layout>
      <HeartbeatContent />
    </Layout>
  );
}
